import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Badge, Button, FormGroup, Input, Label, Table } from 'reactstrap';
import Api from '../../../util/api';
import useConfig from '../../../util/useConfig';
import { formatDateTime } from '../../../util/date-and-time';
import { partial } from '../../../util/translation/translation';

const p = partial('chat');
const s = partial('shared');

const STATUS_COLORS = {
  scheduled: 'primary',
  posting: 'info',
  posted: 'success',
  cancelled: 'secondary',
  failed: 'danger',
};

// <input type="datetime-local"> value for a date, in local time
const toLocalInput = (date) => moment(date).format('YYYY-MM-DDTHH:mm');

// The club writes in the chat with its name and logo, now or at a set time,
// optionally with a push to everyone the chat is open to
const ChatAnnouncementForm = ({ chatId, onPosted }) => {
  const { id: clubId } = useConfig();
  const [club, setClub] = useState(null);
  const [text, setText] = useState('');
  const [push, setPush] = useState(false);
  const [later, setLater] = useState(false);
  // Poll answers; the message text is the question (2 to 6 answers)
  const [withPoll, setWithPoll] = useState(false);
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [postAt, setPostAt] = useState('');
  // The scheduled announcement being changed, if any
  const [editingId, setEditingId] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!clubId) return;
    Api.clubadmin.profile
      .fetchProfile(clubId)
      .then(({ data }) => setClub(data))
      .catch(() => setClub(null));
  }, [clubId]);

  const fetchScheduled = useCallback(() => {
    Api.chat
      .getScheduledAnnouncements(chatId)
      .then((items) => setScheduled(items || []))
      .catch(() => setScheduled([]));
  }, [chatId]);

  useEffect(() => {
    fetchScheduled();
  }, [fetchScheduled]);

  const filledOptions = pollOptions.map((o) => o.trim()).filter(Boolean);
  const pollReady = !withPoll || filledOptions.length >= 2;

  const reset = () => {
    setText('');
    setWithPoll(false);
    setPollOptions(['', '']);
    setPush(false);
    setLater(false);
    setPostAt('');
    setEditingId(null);
  };

  const send = async () => {
    if (!text.trim() || !club?.name || (later && !postAt) || !pollReady) {
      return;
    }
    setSending(true);
    try {
      const time = later ? new Date(postAt).toISOString() : undefined;
      if (editingId) {
        await Api.chat.updateScheduledAnnouncement(editingId, {
          text: text.trim(),
          push,
          postAt: time,
        });
        toast.success(p('announcementRescheduled'));
      } else {
        await Api.chat.postAnnouncement(chatId, {
          text: text.trim(),
          clubName: club.name,
          clubImage: club.imageUrl || undefined,
          push,
          postAt: time,
          pollOptions: withPoll ? filledOptions : undefined,
        });
        if (later) {
          toast.success(
            p('announcementScheduled', { time: formatDateTime(time) })
          );
        } else {
          toast.success(
            push ? p('announcementPushed') : p('announcementPosted')
          );
        }
      }
      reset();
      fetchScheduled();
      if (!later) onPosted?.();
    } catch (err) {
      toast.error(err || p('announcementERROR'));
    } finally {
      setSending(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setText(item.text);
    setPush(item.push);
    setLater(true);
    setPostAt(toLocalInput(item.postAt));
  };

  const cancel = async (item) => {
    try {
      await Api.chat.cancelScheduledAnnouncement(item.id);
      toast.success(p('announcementCancelled'));
      if (editingId === item.id) reset();
      fetchScheduled();
    } catch (err) {
      toast.error(err || p('announcementERROR'));
    }
  };

  return (
    <div className="mb-3">
      <FormGroup>
        <Label for="announcement-text">
          {p('announcementAs', { name: club?.name || '' })}
        </Label>
        <Input
          id="announcement-text"
          type="textarea"
          rows={3}
          maxLength={1000}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </FormGroup>
      <FormGroup check className="mb-2">
        <Label check>
          <Input
            type="checkbox"
            checked={withPoll}
            disabled={!!editingId}
            onChange={(e) => setWithPoll(e.target.checked)}
          />{' '}
          {p('announcementPoll')}
        </Label>
      </FormGroup>
      {withPoll && (
        <div className="mb-2" style={{ maxWidth: 420 }}>
          {pollOptions.map((option, i) => (
            <div key={i} className="d-flex mb-1">
              <Input
                bsSize="sm"
                maxLength={80}
                placeholder={p('pollOption', { number: i + 1 })}
                value={option}
                onChange={(e) =>
                  setPollOptions((current) =>
                    current.map((item, j) => (j === i ? e.target.value : item))
                  )
                }
              />
              {pollOptions.length > 2 && (
                <Button
                  size="sm"
                  color="link"
                  className="mb-0 ml-1"
                  onClick={() =>
                    setPollOptions((current) =>
                      current.filter((_, j) => j !== i)
                    )
                  }
                >
                  ✕
                </Button>
              )}
            </div>
          ))}
          {pollOptions.length < 6 && (
            <Button
              size="sm"
              color="link"
              className="mb-0 p-0"
              onClick={() => setPollOptions((current) => [...current, ''])}
            >
              {p('pollAddOption')}
            </Button>
          )}
          <div>
            <small className="text-muted">{p('pollInfo')}</small>
          </div>
        </div>
      )}
      <FormGroup check className="mb-2">
        <Label check>
          <Input
            type="checkbox"
            checked={push}
            onChange={(e) => setPush(e.target.checked)}
          />{' '}
          {p('announcementPush')}
        </Label>
      </FormGroup>
      <FormGroup check className="mb-2">
        <Label check>
          <Input
            type="checkbox"
            checked={later}
            disabled={!!editingId}
            onChange={(e) => setLater(e.target.checked)}
          />{' '}
          {p('announcementLater')}
        </Label>
      </FormGroup>
      {later && (
        <FormGroup style={{ maxWidth: 260 }}>
          <Input
            type="datetime-local"
            value={postAt}
            min={toLocalInput(new Date())}
            onChange={(e) => setPostAt(e.target.value)}
          />
        </FormGroup>
      )}
      <Button
        size="sm"
        color="primary"
        className="mb-0 mr-2"
        disabled={
          sending ||
          !text.trim() ||
          !club?.name ||
          (later && !postAt) ||
          !pollReady
        }
        onClick={send}
      >
        {editingId && p('announcementSave')}
        {!editingId && later && p('announcementSchedule')}
        {!editingId && !later && p('announcementSend')}
      </Button>
      {editingId && (
        <Button size="sm" className="mb-0" onClick={reset}>
          {s('cancel')}
        </Button>
      )}

      {scheduled.length > 0 && (
        <Table responsive size="sm" className="mt-3 mb-0">
          <thead>
            <tr>
              <th>{p('announcementWhen')}</th>
              <th>{p('message')}</th>
              <th>{p('announcementPushShort')}</th>
              <th>{p('announcementStatus')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {scheduled.map((item) => (
              <tr key={item.id}>
                <td className="text-nowrap">{formatDateTime(item.postAt)}</td>
                <td style={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>
                  {item.text}
                </td>
                <td>{item.push ? '✓' : '–'}</td>
                <td>
                  <Badge color={STATUS_COLORS[item.status] || 'secondary'}>
                    {p(`announcementStatus_${item.status}`)}
                  </Badge>
                  {item.status === 'failed' && item.error && (
                    <div className="text-muted small">
                      {FAILURE_REASONS[item.error]
                        ? p(FAILURE_REASONS[item.error])
                        : item.error}
                    </div>
                  )}
                </td>
                <td className="text-nowrap text-right">
                  {item.status === 'scheduled' && (
                    <>
                      <Button
                        size="sm"
                        color="link"
                        className="mb-0 p-0 mr-2"
                        onClick={() => edit(item)}
                      >
                        {s('edit')}
                      </Button>
                      <Button
                        size="sm"
                        color="link"
                        className="mb-0 p-0 text-danger"
                        onClick={() => cancel(item)}
                      >
                        {s('cancel')}
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

// Why a scheduled announcement was not posted, by the backend's error key
const FAILURE_REASONS = {
  'chat.TooLate': 'announcementError_tooLate',
  'chat.Inactive': 'announcementError_closed',
  'chat.PostInterrupted': 'announcementError_interrupted',
};

export default ChatAnnouncementForm;
